// The signature order looks (R2408, R2409, ui-map-interface.md §6.1.12,
// mockup round 105).
//
// Card 01 Snap Ring is the activation of every spend: the spend look, the
// hold look while the order holds and the end look. Each order adds the
// look the owner picked: OUTFLANK card 08 Ghost Arc, BRACE card 12 Clamp
// Brackets, CANISTER card 13 Iris, DIG IN card 18 Bow All Round,
// BROADSIDE card 20 Gun Ports and CRASH DIVE card 22 Fade Down. Card 24
// Silence Shroud is the look of the DEEP RUN gait (R2409).
//
// This module is the leaf of the look: the clock shares and the packs of
// each mark into the engine's batches. It keeps no state and allocates
// nothing, so the `signature` layer and the fleet layer both call it once
// a frame. Every figure is `[presentation.signature]`.

import { SignatureOrder } from '../gen/proto/aquaphobia';
import { SIGNATURE as S } from '../gen/presentation';
import type { CircleSink, QuadSink, SegmentSink, SpriteEntry } from '../fast/layers/batches';
import { angleGap } from './bow';
import { TAU, clamp01, dashesOn } from './math';
import { CORE_INK, DEEP_INK, PALE_INK, cellScale, outCubic } from './recipe-kit';

/** One degree in radians. */
const DEG = Math.PI / 180;
/** The white of a hit and of a pellet: the flash of the §6.3.3 shot, the
 *  core ink of the kits (white in the dark identity, `core` on paper,
 *  R3293). */
const WHITE_INK = CORE_INK;

/** The shares the fleet layer reads off the signature layer (R2408). */
export interface SignatureMarks {
  /** The share of the DIG IN close of the rear dead zone, 0 to 1, or -1
   *  when the layer holds no DIG IN look for the hull. */
  digClose(fleetId: number): number;
  /** The share of CANISTER's iris, 0 (shut) to 1 (open). */
  irisOpen(fleetId: number): number;
  /** The share of the CRASH DIVE fade of an own hull, 0 to 1. */
  diveShare(fleetId: number): number;
}

/** One hull as the signature layer draws it (`FleetLayer.signaturePose`). */
export interface SignaturePose {
  x: number;
  y: number;
  /** The drawn bow in radians: +x is the bow. */
  head: number;
  faction: number;
  cls: number;
  own: boolean;
  alpha: number;
  /** The hull's sonar reach in world px; own hulls only, else 0. */
  sonarPx: number;
  /** The course end of an own hull, in world px. */
  tx: number;
  ty: number;
  /** The mark scale (§6.1.9). */
  k: number;
  /** The hollow hull cell of the class in the look in force. */
  cell: string;
}

/** The three batches a look writes. */
export interface SignatureSinks {
  quads: QuadSink;
  rings: CircleSink;
  segs: SegmentSink;
  sprite(key: string): SpriteEntry;
}

// ---------------------------------------------------------------------------
// The clock
// ---------------------------------------------------------------------------

/** An order whose recharge floor stands still while its effect holds and
 *  runs from the effect's end: CANISTER and BROADSIDE (R2412). */
export function floorHeldOrder(order: SignatureOrder): boolean {
  return order === SignatureOrder.SIGNATURE_ORDER_CANISTER
    || order === SignatureOrder.SIGNATURE_ORDER_BROADSIDE;
}

/** The share of a look of `sec` wall seconds at `age`, or -1 when it does
 *  not run (before the start or past the end). */
export function runShare(age: number, sec: number): number {
  return age >= 0 && age < sec ? age / sec : -1;
}

/**
 * The share of the countdown of a timed order, 1 (full) to 0. `left` is
 * the whole MAP seconds of the last frame, rounded up; `first` the first
 * figure the layer saw after the spend (card 01); `since` the wall seconds
 * since `left` arrived. The run down between two frames moves at the map
 * rate and never passes the second the server rounded.
 */
export function countdownShare(left: number, first: number, since: number, mapPerWall: number): number {
  if (!(first > 0)) return 1;
  const run = Math.min(1, Math.max(0, since * (mapPerWall > 0 ? mapPerWall : 0)));
  return clamp01((left - run) / first);
}

/**
 * A share that grows over `inSec` from the onset and falls over `outSec`
 * from the end. `onAge` is the wall seconds since the onset (Infinity for
 * a hold the client did not see begin); `offAge` the wall seconds since the
 * end, or a negative number while the hold stands. An end before the share
 * is full falls from the share it reached.
 */
export function holdShare(onAge: number, offAge: number, inSec: number, outSec: number): number {
  if (offAge < 0) return outCubic(onAge / inSec);
  const reached = outCubic((onAge - offAge) / inSec);
  return reached * (1 - outCubic(offAge / outSec));
}

// ---------------------------------------------------------------------------
// Card 01: the snap ring
// ---------------------------------------------------------------------------

/** The spend look at share `u` of `spend_sec`. */
export function packSpend(c: CircleSink, x: number, y: number, u: number, ink: number, k: number): void {
  const r = S.snapFromPx + (S.snapToPx - S.snapFromPx) * outCubic(u / S.snapGrowShare);
  const w = S.snapThinPx + (S.snapWidthPx - S.snapThinPx) * (1 - u);
  c.ring(x, y, r * k, w * k, ink, S.snapAlpha * Math.pow(1 - u, 1.3));
  if (u > S.echoAfterShare) {
    const v = (u - S.echoAfterShare) / (1 - S.echoAfterShare);
    const re = S.snapFromPx + (S.echoToPx - S.snapFromPx) * outCubic(v / S.echoGrowShare);
    c.ring(x, y, re * k, S.echoWidthPx * k, ink, S.echoAlpha * (1 - v));
  }
  if (u < S.flashShare) c.disc(x, y, S.flashPx * k, ink, S.flashAlpha * (1 - u / S.flashShare));
}

/**
 * The hold look: a timed order (`share` 0 to 1) draws a faint track and an
 * arc from the bow that drains; an order with no clock (`share` null)
 * draws a dashed ring that turns, `turnSec` the wall seconds of the hold.
 */
export function packHold(
  c: CircleSink, x: number, y: number, head: number, share: number | null, turnSec: number,
  ink: number, k: number,
): void {
  const r = S.holdPx * k;
  if (share !== null) {
    c.ring(x, y, r, S.holdWidthPx * k, ink, S.holdTrackAlpha);
    if (share > 0.002) c.arc(x, y, r, S.holdWidthPx * k, head, share * TAU, ink, S.holdArcAlpha);
    return;
  }
  const turn = (turnSec * S.holdDashTurnPxPerSec) / S.holdPx;
  c.dashed(x, y, r, S.holdDashWidthPx * k, S.holdDashCount, S.holdDashFill, ink, S.holdDashAlpha, turn);
}

/** The end look at share `u` of `end_sec`: the ring draws in. */
export function packEnd(c: CircleSink, x: number, y: number, u: number, ink: number, k: number): void {
  const r = S.holdPx - (S.holdPx - S.endToPx) * outCubic(u);
  c.ring(x, y, r * k, S.holdWidthPx * k, ink, S.endAlpha * (1 - u));
  if (u < S.endFlashShare) c.disc(x, y, S.endFlashPx * k, ink, S.endFlashAlpha * (1 - u / S.endFlashShare));
}

// ---------------------------------------------------------------------------
// OUTFLANK, card 08: the lane, the stern mark and the ghosts
// ---------------------------------------------------------------------------

/**
 * Where the stern mark stands: on the ray from the target `t` through the
 * stern point `s`, at the stern point or past the marks astern of the
 * target by `stern_mark_clear_px`, whichever is further. The marks astern
 * are the charge pip (§6.1.8) and the stern chevrons (§6.1.11, R2417), and
 * their outer edge stands `edgePx` astern. Writes `out`.
 */
export function sternMark(
  tx: number, ty: number, sx: number, sy: number, edgePx: number,
  out: { x: number; y: number },
): { x: number; y: number } {
  const dx = sx - tx, dy = sy - ty;
  const d = Math.hypot(dx, dy);
  const clear = edgePx + S.sternMarkClearPx + S.sternMarkPx;
  if (d < 1e-6) { out.x = sx; out.y = sy; return out; }
  const m = Math.max(d, clear);
  out.x = tx + (dx / d) * m;
  out.y = ty + (dy / d) * m;
  return out;
}

/**
 * The lane of card 08: a broad faint polyline from the hull round the
 * target to the stern mark, in polar steps about the target, the short way
 * round. `grow` is the share of the lane drawn from the hull (0 to 1).
 */
export function packLane(
  segs: SegmentSink, hx: number, hy: number, tx: number, ty: number, mx: number, my: number,
  grow: number, ink: number, alpha: number,
): void {
  const n = S.lanePoints;
  const a0 = Math.atan2(hy - ty, hx - tx), r0 = Math.hypot(hx - tx, hy - ty);
  const a1 = Math.atan2(my - ty, mx - tx), r1 = Math.hypot(mx - tx, my - ty);
  const da = angleGap(a0, a1);
  let px = hx, py = hy;
  for (let i = 1; i <= n; i++) {
    const s = (i / n) * grow;
    const a = a0 + da * s, r = r0 + (r1 - r0) * s;
    const x = tx + Math.cos(a) * r, y = ty + Math.sin(a) * r;
    segs.line(px, py, x, y, S.laneWidthPx, ink, alpha);
    px = x; py = y;
  }
}

/** The turns of the three corners of the stern mark off the bearing. */
const STERN_TURNS = [2.3, -2.3, 0] as const;

/** The stern mark: a small triangle at `m` that points at the target. */
export function packSternMark(
  segs: SegmentSink, mx: number, my: number, tx: number, ty: number, ink: number, alpha: number,
  round: number,
): void {
  const at = Math.atan2(ty - my, tx - mx), r = S.sternMarkPx;
  let px = mx + Math.cos(at) * r, py = my + Math.sin(at) * r;
  for (let i = 0; i < 3; i++) {
    const off = STERN_TURNS[i];
    const x = mx + Math.cos(at + off) * r, y = my + Math.sin(at + off) * r;
    segs.line(px, py, x, y, S.sternMarkWidthPx, ink, alpha, round);
    px = x; py = y;
  }
}

/** The alpha of ghost `j` (0 nearest the hull). */
export function ghostAlpha(j: number): number {
  return j === 0 ? S.ghostAlphaNear : j === 1 ? S.ghostAlphaMid : S.ghostAlphaFar;
}

// ---------------------------------------------------------------------------
// BRACE, card 12: the trace, the brackets and the glances
// ---------------------------------------------------------------------------

/** The trace of the snap at share `u`: an arc from `from` to `to` with an
 *  arrowhead at `to`. A snap under a twentieth of a radian draws none. */
export function packTrace(
  c: CircleSink, segs: SegmentSink, x: number, y: number, from: number, to: number, u: number,
  ink: number, k: number, round: number,
): void {
  const d = angleGap(from, to);
  if (Math.abs(d) < 0.05) return;
  const r = S.tracePx * k, a = S.traceAlpha * (1 - u), w = S.traceWidthPx * k;
  c.arc(x, y, r, w, d >= 0 ? from : from + d, Math.abs(d), ink, a);
  const px = x + Math.cos(to) * r, py = y + Math.sin(to) * r;
  const along = to + Math.sign(d) * Math.PI / 2, hp = S.traceHeadPx * k;
  for (let e = -0.5; e <= 0.5; e += 1) {
    segs.line(px, py, px - Math.cos(along + e) * hp, py - Math.sin(along + e) * hp, w, ink, a, round);
  }
}

/**
 * The two brackets on the bow quarters. `close` is the share of the close
 * (0 wide, 1 shut), `left` the share of the order left (1 full), `a` the
 * alpha factor of the end.
 */
export function packClamp(
  c: CircleSink, segs: SegmentSink, x: number, y: number, head: number,
  close: number, left: number, a: number, ink: number, k: number, round: number,
): void {
  const r = (S.clampFromPx + (S.clampToPx - S.clampFromPx) * close + (1 - left) * S.clampOpenPx) * k;
  const half = S.clampHalfDeg * DEG, w = S.clampWidthPx * k, al = S.clampAlpha * a;
  for (let sgn = -1; sgn <= 1; sgn += 2) {
    const mid = head + sgn * S.clampBearingDeg * DEG;
    c.arc(x, y, r, w, mid - half, 2 * half, ink, al);
    // The two ends of the arc: `mid + -1 * half` is `mid - half` to the bit.
    for (let end = -1; end <= 1; end += 2) {
      const e = mid + end * half;
      const cx = Math.cos(e), cy = Math.sin(e), t = S.clampTickPx * k;
      segs.line(x + cx * r, y + cy * r, x + cx * (r - t), y + cy * (r - t), w, ink, al, round);
    }
  }
}

/** One hit that glances off at share `u`: `bearing` is toward the shooter. */
export function packGlance(
  segs: SegmentSink, x: number, y: number, bearing: number, u: number, k: number, round: number,
): void {
  const px = x + Math.cos(bearing) * S.glanceFromPx * k, py = y + Math.sin(bearing) * S.glanceFromPx * k;
  const len = (S.glanceLenPx + S.glanceGrowPx * outCubic(u)) * k, e = bearing + S.glanceTurnDeg * DEG;
  segs.line(px, py, px + Math.cos(e) * len, py + Math.sin(e) * len,
    S.glanceWidthPx * k, WHITE_INK, S.glanceAlpha * (1 - u), round);
}

// ---------------------------------------------------------------------------
// CANISTER, card 13: the pellet fan (the iris is the fleet layer's)
// ---------------------------------------------------------------------------

/** The canister shot at share `u` of `pellet_sec`, toward `bearing`. */
export function packPellets(
  c: CircleSink, x: number, y: number, bearing: number, u: number, ink: number, k: number,
): void {
  if (u < S.muzzleShare) {
    const m = S.muzzleAtPx * k;
    c.disc(x + Math.cos(bearing) * m, y + Math.sin(bearing) * m, S.muzzlePx * k, ink,
      S.muzzleAlpha * (1 - u / S.muzzleShare));
  }
  const n = S.pelletCount, d = S.pelletFromPx * k + S.pelletReachPx * outCubic(u);
  for (let j = 0; j < n; j++) {
    const a = bearing + (j - (n - 1) / 2) * S.pelletSpreadDeg * DEG;
    const px = x + Math.cos(a) * d, py = y + Math.sin(a) * d;
    c.disc(px, py, S.pelletHaloPx * k, ink, S.pelletHaloAlpha * (1 - u));
    c.disc(px, py, S.pelletPx * k, WHITE_INK, S.pelletAlpha * (1 - u * u));
  }
}

// ---------------------------------------------------------------------------
// DIG IN, card 18: four bows
// ---------------------------------------------------------------------------

/** The quarter turns and the delays (in chevron steps) of the four bows. */
const BOWS: readonly (readonly [number, number])[] = [[0, 0], [1, 1], [-1, 1], [2, 2]];

/** The share of bow `j` of the four (0 bow, 1 and 2 the beams, 3 the
 *  stern) at the ages of the hold. */
export function bowShare(j: number, onAge: number, offAge: number): number {
  const delay = BOWS[j][1] * S.chevronStepSec;
  if (offAge >= 0) {
    const reached = outCubic((onAge - offAge - delay) / S.chevronTurnSec);
    return reached * (1 - outCubic(offAge / S.chevronFoldSec));
  }
  return outCubic((onAge - delay) / S.chevronTurnSec);
}

/** The four outward chevrons at about r21: the bow copied to the beams and
 *  the stern. */
export function packBows(
  segs: SegmentSink, x: number, y: number, head: number, onAge: number, offAge: number,
  ink: number, k: number, round: number,
): void {
  for (let j = 0; j < BOWS.length; j++) {
    const g = bowShare(j, onAge, offAge);
    if (g <= 0.01) continue;
    const a = head + BOWS[j][0] * (Math.PI / 2) * g;
    const cx = Math.cos(a), cy = Math.sin(a), px = -cy, py = cx;
    const tip = S.chevronTipPx * k, back = S.chevronBackPx * k, h = S.chevronHalfPx * k;
    const tx = x + cx * tip, ty = y + cy * tip, bx = x + cx * back, by = y + cy * back;
    const al = S.chevronAlpha * Math.min(1, g * 2), w = S.chevronWidthPx * k;
    segs.line(bx + px * h, by + py * h, tx, ty, w, ink, al, round);
    segs.line(tx, ty, bx - px * h, by - py * h, w, ink, al, round);
  }
}

// ---------------------------------------------------------------------------
// BROADSIDE, card 20: the gun ports
// ---------------------------------------------------------------------------

/** The side of the hull that faces the point (`fx`, `fy`): 1 or -1. */
export function sideOf(x: number, y: number, head: number, fx: number, fy: number): number {
  const cross = Math.cos(head) * (fy - y) - Math.sin(head) * (fx - x);
  return cross < 0 ? -1 : 1;
}

/**
 * The ports on side `side`. `glow` is the share of the glow while the
 * order holds (0 to 1); it also scales the ports, so they fade out with
 * the glow after the order. `flash` is the share of the flash of a shot,
 * or -1. Every shot of the window flashes (R2856).
 */
export function packPorts(
  c: CircleSink, segs: SegmentSink, x: number, y: number, head: number, side: number,
  glow: number, flash: number, ink: number, k: number, round: number,
): void {
  const cx = Math.cos(head), cy = Math.sin(head);
  const nrm = head + side * Math.PI / 2;
  for (let j = 0; j < S.portCount; j++) {
    const lx = (-S.portAftPx + j * S.portStepPx) * k, ly = side * S.portSidePx * k;
    const px = x + cx * lx - cy * ly, py = y + cy * lx + cx * ly;
    c.disc(px, py, (S.portPx + S.portHaloPx * glow) * k, ink, S.portHaloAlpha * glow);
    c.disc(px, py, S.portPx * k, ink, S.portAlpha * glow);
    if (flash < 0) continue;
    const len = (S.portFlashFromPx + S.portFlashGrowPx * outCubic(flash)) * k;
    for (let spread = -1; spread <= 1; spread++) {
      const a = nrm + spread * S.portFlashSpreadDeg * DEG;
      segs.line(px, py, px + Math.cos(a) * len, py + Math.sin(a) * len,
        S.portFlashWidthPx * k, WHITE_INK, S.portFlashAlpha * (1 - flash), round);
    }
  }
}

// ---------------------------------------------------------------------------
// CRASH DIVE, card 22: the noise footprint (the fade is the fleet layer's)
// ---------------------------------------------------------------------------

/**
 * The radius of the footprint: the sonar reach `r0` shrinks to `r0 · mul`
 * over `foot_shrink_sec` and grows back over `foot_grow_sec` after the
 * order.
 */
export function footRadius(r0: number, mul: number, onAge: number, offAge: number): number {
  const lo = r0 * mul;
  if (offAge >= 0) {
    const reached = r0 - (r0 - lo) * outCubic((onAge - offAge) / S.footShrinkSec);
    return reached + (r0 - reached) * outCubic(offAge / S.footGrowSec);
  }
  return r0 - (r0 - lo) * outCubic(onAge / S.footShrinkSec);
}

/** The dotted footprint at radius `r`, with its two echoes while it
 *  shrinks (`echo` the share of the shrink, or -1). */
export function packFootprint(
  c: CircleSink, x: number, y: number, r: number, r0: number, a: number, echo: number, ink: number,
): void {
  const step = S.footDotOnPx + S.footDotOffPx;
  c.dashed(x, y, r, S.footWidthPx, dashesOn(r, S.footDotOnPx, S.footDotOffPx), S.footDotOnPx / step,
    ink, S.footAlpha * a);
  if (echo < 0) return;
  for (let i = 0; i < 2; i++) {
    const m = i ? S.footEchoFar : S.footEchoNear;
    const re = Math.min(r0, r * m);
    c.ring(x, y, re, S.footEchoWidthPx, ink, S.footEchoAlpha * (1 - echo));
  }
}

// ---------------------------------------------------------------------------
// GETAWAY of the Dredger (R2857): the crates and the wake
// ---------------------------------------------------------------------------

/** A fixed share in [0, 1) for crate `j`: the crates of one drop differ,
 *  and every frame draws each one in the same place. */
function crateJitter(j: number): number {
  const s = Math.sin(j * 12.9898 + 78.233) * 43758.5453;
  return s - Math.floor(s);
}

/**
 * The cargo that GETAWAY drops, at share `u` of `getaway_crate_sec`: the
 * crates fall away astern of the point of the drop (`x`, `y`, with the bow
 * `head` at the drop), turn, shrink and fade. Each crate is one square: a
 * butt-capped segment as long as it is wide.
 */
export function packCrates(
  segs: SegmentSink, x: number, y: number, head: number, u: number, ink: number, k: number,
): void {
  const n = S.getawayCrateCount, e = outCubic(u);
  const size = S.getawayCratePx * (1 - (1 - S.getawayCrateSinkShare) * u) * k;
  const alpha = S.getawayCrateAlpha * (1 - u * u);
  for (let j = 0; j < n; j++) {
    const q = crateJitter(j);
    const a = head + Math.PI + (n > 1 ? (j / (n - 1) - 0.5) * 2 : 0) * S.getawayCrateSpreadDeg * DEG;
    const d = (S.getawayCrateFromPx + S.getawayCrateDriftPx * e * (0.6 + 0.4 * q)) * k;
    const cx = x + Math.cos(a) * d, cy = y + Math.sin(a) * d;
    const spin = a + (q - 0.5) * 2 * S.getawayCrateSpinDeg * DEG * e;
    const hx = Math.cos(spin) * size / 2, hy = Math.sin(spin) * size / 2;
    segs.line(cx - hx, cy - hy, cx + hx, cy + hy, size, ink, alpha, 0);
  }
}

/** The stretched wake of a hull under GETAWAY: two lines astern of the
 *  keel at share `g` (0 to 1) of their grow. */
export function packRunWake(
  segs: SegmentSink, x: number, y: number, head: number, g: number, ink: number, k: number, round: number,
): void {
  if (g <= 0) return;
  const from = S.getawayWakeFromPx * k, len = S.getawayWakeLenPx * g * k;
  for (let side = -1; side <= 1; side += 2) {
    const a = head + Math.PI + side * S.getawayWakeSpreadDeg * DEG;
    const cx = Math.cos(a), cy = Math.sin(a);
    segs.line(x + cx * from, y + cy * from, x + cx * (from + len), y + cy * (from + len),
      S.getawayWakeWidthPx * k, ink, S.getawayWakeAlpha * g, round);
  }
}

// ---------------------------------------------------------------------------
// The DEEP RUN gait, card 24: the shroud and the bubbles (R2409)
// ---------------------------------------------------------------------------

/** The soft cell the shroud draws from: the effect disc of §6.6. */
const SHROUD_CELL = 'effect:disc';

/** The shroud at share `g`: a soft disc of the deep ink over the hull. */
export function packShroud(sk: SignatureSinks, x: number, y: number, g: number, k: number): void {
  if (!(g > 0)) return;
  const e = sk.sprite(SHROUD_CELL);
  sk.quads.sprite(x, y, 0, e, DEEP_INK, S.shroudAlpha * g, cellScale(e, S.shroudPx * k));
}

/** The bubbles that rise as the gait turns on, at share `u`. They stand
 *  upright on the screen, as bubbles rise. */
export function packBubbles(c: CircleSink, x: number, y: number, u: number, k: number): void {
  for (let j = 0; j < S.bubbleCount; j++) {
    const bx = x + (-S.bubbleAftPx + j * S.bubbleStepPx) * k;
    const by = y - S.bubbleUpPx * k - (S.bubbleRisePx + j * S.bubbleRiseStepPx) * outCubic(u) * k;
    const r = (j % 2 === 1 ? S.bubbleOddPx : S.bubblePx) * k;
    c.ring(bx, by, r, S.bubbleWidthPx * k, PALE_INK, S.bubbleAlpha * (1 - u));
  }
}
